import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Heart, Sparkles, TreePine, HeartHandshake, Target, Trees } from "lucide-react";

const PRESETS = [0.5, 2, 5, 10, 25, 50, 100];

export default function Donate() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [amount, setAmount] = useState(10);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  // "one_time" (una tantum) or "month" (mensile). Only enabled for platform
  // and per-reforestation project donations — a subscription to a single
  // report has no meaning.
  const [interval, setInterval] = useState("one_time");

  useEffect(() => {
    api.get("/reforestation/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const donate = async () => {
    const val = parseFloat(amount);
    if (val < 0.5) { toast.error(t("donate_min_error")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        amount: val,
        currency: "eur",
        target_type: "platform",
        origin_url: window.location.origin,
        message: msg,
        interval,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("donate_stripe_error"));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="donate-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="donate-back-btn" />
        <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-600 to-emerald-800 text-white p-10 shadow-xl mb-8">
          <div className="inline-flex items-center gap-2 chip bg-white/20 text-white mb-4"><Heart size={12} /> {t("donation_title")}</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">{t("donation_title")}</h1>
          <p className="mt-3 opacity-90 max-w-2xl">{t("donation_desc")}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          {/* One-time vs monthly toggle. Same amount rules apply either way. */}
          <div className="flex items-center gap-2 mb-4" data-testid="donate-interval-toggle">
            <button
              type="button"
              data-testid="donate-interval-one-time"
              onClick={() => setInterval("one_time")}
              className={`h-9 px-4 rounded-full text-sm font-semibold border transition ${interval === "one_time" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
            >{t("one_time")}</button>
            <button
              type="button"
              data-testid="donate-interval-monthly"
              onClick={() => setInterval("month")}
              className={`h-9 px-4 rounded-full text-sm font-semibold border transition inline-flex items-center gap-1.5 ${interval === "month" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
            >
              <HeartHandshake size={13} /> {t("monthly")}
            </button>
            {user && (
              <button
                type="button"
                data-testid="donate-manage-subscriptions"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
                    if (data?.portal_url) window.location.href = data.portal_url;
                  } catch (e) {
                    toast.info(e?.response?.data?.detail || t("no_active_subscriptions"));
                  }
                }}
                className="ml-auto h-9 px-3 rounded-full text-xs font-semibold border bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              >{t("manage_subscriptions")}</button>
            )}
          </div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("donation_amount")} (EUR){interval === "month" ? " / mese" : ""}</Label>
          <div className="mt-3 grid grid-cols-4 sm:grid-cols-7 gap-2">
            {PRESETS.map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`donate-preset-${v}`}
                onClick={() => setAmount(v)}
                className={`h-12 rounded-full font-semibold text-sm ${amount === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >€{v}</button>
            ))}
          </div>
          <div className="mt-4">
            <Input data-testid="donate-amount-input" type="number" min="0.50" step="0.10" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-xl text-lg font-semibold" />
            <div className="text-xs text-slate-500 mt-1">{t("donate_min_amount")}</div>
          </div>

          <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-6 block">{t("donation_message")}</Label>
          <Textarea data-testid="donate-message" value={msg} onChange={(e) => setMsg(e.target.value)} className="mt-1.5 rounded-xl" placeholder={t("donate_message_placeholder")} />

          <Button data-testid="donate-submit-btn" onClick={donate} disabled={busy} size="lg" className="w-full mt-6 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-base font-semibold">
            <Sparkles size={16} className="mr-2" /> {busy ? t("donate_redirect") : (interval === "month" ? `Attiva donazione mensile` : t("donate_now"))}
          </Button>
          <div className="mt-4 text-xs text-slate-500 text-center">{t("donate_secure")} <b>4242 4242 4242 4242</b></div>
        </div>

        {/* Reforestation projects — same Stripe checkout flow, free amount */}
        <div className="mt-10" data-testid="donate-reforestation-section">
          <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold flex items-center gap-2"><TreePine size={12} /> Progetto TO</div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mt-1">{t("support_reforestation")}</h2>
              <p className="text-sm text-slate-600 mt-1">Ogni contributo va a un progetto reale, con specie locali e monitoraggio nel tempo. Importo libero.</p>
            </div>
            <Link to="/reforestation" data-testid="donate-reforestation-all" className="text-xs font-semibold text-emerald-700 hover:underline whitespace-nowrap">Vedi tutti i progetti →</Link>
          </div>

          {projects.length === 0 && (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-slate-500 text-sm" data-testid="donate-reforestation-empty">
              <TreePine className="mx-auto mb-2 text-emerald-600" size={24} />
              Nessun progetto attualmente pubblicato. Torna presto.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {projects.slice(0, 4).map((p) => (
              <ReforestationDonateCard key={p.project_id} p={p} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReforestationDonateCard({ p }) {
  const { t, fmtCurrency } = useI18n();
  const [amount, setAmount] = useState(10);
  const [busy, setBusy] = useState(false);
  const [interval, setInterval] = useState("one_time");
  const pct = p.goal_amount > 0 ? Math.min(100, Math.round((p.raised_amount || 0) / p.goal_amount * 100)) : 0;

  const donate = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 0.5) { toast.error(t("donate_min_error")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        amount: amt, currency: "eur",
        target_type: "reforestation", target_id: p.project_id,
        message: `Riforestazione: ${p.title}`,
        origin_url: window.location.origin,
        interval,
      });
      const url = data.checkout_url || data.url;
      if (url) window.location.href = url;
      else { toast.error(t("donate_stripe_error")); setBusy(false); }
    } catch (e) {
      const d = e.response?.data?.detail;
      const m = typeof d === "string" ? d : (Array.isArray(d) ? d.map((x) => x.msg || "").join(", ") : t("donate_stripe_error"));
      toast.error(m); setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`donate-reforest-card-${p.project_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Badge className="bg-emerald-100 text-emerald-800 border-0 uppercase text-[10px]">{p.status}</Badge>
        {p.location_name && <span className="text-xs text-slate-500 truncate">{p.location_name}</span>}
      </div>
      <h3 className="font-heading text-base font-bold text-slate-900 break-words" data-testid={`donate-reforest-title-${p.project_id}`}>{p.title}</h3>
      <p className="text-xs text-slate-600 mt-1 line-clamp-2 break-words">{p.description}</p>
      {p.goal_amount > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-[10px] text-slate-500 mb-1"><span><Target size={9} className="inline mr-1" /> {fmtCurrency(p.goal_amount)}</span><span>{fmtCurrency(p.raised_amount || 0)} · {pct}%</span></div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
      {p.trees_goal > 0 && (
        <div className="text-[10px] text-slate-500 mt-1.5"><Trees size={9} className="inline mr-1" /> {p.trees_planted||0} / {p.trees_financed||0} / {p.trees_goal} alberi</div>
      )}

      <div className="mt-3 grid grid-cols-4 gap-1.5" data-testid={`donate-reforest-presets-${p.project_id}`}>
        {PRESETS.map((v) => (
          <button
            key={v}
            type="button"
            data-testid={`donate-reforest-preset-${p.project_id}-${v}`}
            onClick={() => setAmount(v)}
            className={`h-8 rounded-full font-semibold text-xs ${Number(amount) === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
          >€{v}</button>
        ))}
      </div>
      {/* Per-project monthly toggle. Same behaviour as the platform card. */}
      <div className="mt-2 flex items-center gap-1.5" data-testid={`donate-reforest-interval-${p.project_id}`}>
        <button
          type="button"
          data-testid={`donate-reforest-interval-one-time-${p.project_id}`}
          onClick={() => setInterval("one_time")}
          className={`h-7 px-3 rounded-full text-[11px] font-semibold border ${interval === "one_time" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700"}`}
        >{t("one_time")}</button>
        <button
          type="button"
          data-testid={`donate-reforest-interval-monthly-${p.project_id}`}
          onClick={() => setInterval("month")}
          className={`h-7 px-3 rounded-full text-[11px] font-semibold border inline-flex items-center gap-1 ${interval === "month" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-700"}`}
        >
          <HeartHandshake size={10} /> {t("monthly")}
        </button>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input
          type="number" min="0.50" step="0.10" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-24 h-9 rounded-full text-sm"
          data-testid={`donate-reforest-amount-${p.project_id}`}
          placeholder="€"
        />
        <Button
          data-testid={`donate-reforest-btn-${p.project_id}`}
          onClick={donate}
          disabled={busy}
          size="sm"
          className="flex-1 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
        >
          <HeartHandshake size={13} className="mr-1.5" /> {busy ? t("donate_redirect") : "Dona ora"}
        </Button>
      </div>
      <div className="text-[10px] text-slate-500 mt-1">{t("donate_min_amount")}</div>
    </div>
  );
}
